import React from "react";
import styled from "styled-components";
import { Form } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronUp } from "@fortawesome/pro-solid-svg-icons";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router-dom";
import { default as UUID } from "uuid";
import {
  FullAccess,
  Lesson,
  SearchResponse,
  SearchResultKind,
  Unit,
} from "../common/api";
import { AccountContext } from "../common/context/AccountContext";
import { ApiContext } from "../common/context/ApiContext";
import { useGridColumnCount } from "../common/utils";
import { LessonItem, LessonItemLoggingContext } from "./LessonItem";
import { LeftCornerDecoration } from "./LessonItemBody";
import { UnitItem, UnitItemLoggingContext } from "./UnitItem";
import SearchStandardsBanner from "./SearchStandardsBanner";
import {
  EmptyResults,
  LoadingIndicator,
  PrimaryButton,
  SECTION_SPACING,
  Section,
  SectionHeading,
  UnitTilesRow,
} from "../common/CommonStyles";
import {
  COLOR_SURFACE_CONTAINER,
  COLOR_SURFACE_CONTAINER_STRONG,
  COLOR_TEXT_PRIMARY,
  COLOR_TEXT_SECONDARY,
} from "../common/colors";
import { SPACE_SM, SPACE_MD, SPACE_LG, SPACE_XL } from "../common/spacing";
import {
  GENERIC_ITEM_BORDER_RADIUS,
  LESSON_CARD_MIN_WIDTH,
  UNIT_TILE_MIN_WIDTH,
} from "../common/constants";

export const SEARCH_QUERY_PARAM = "q";

const INITIAL_RESULTS_PER_SECTION = 4;

const FEEDBACK_MAX_LENGTH = 4000;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${SPACE_XL * 3}px 0;
`;

const LessonsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(
    auto-fill,
    minmax(${LESSON_CARD_MIN_WIDTH}px, 1fr)
  );
  row-gap: ${SPACE_LG}px;
  column-gap: ${SPACE_XL}px;
  padding: ${SECTION_SPACING}px 0;

  & > * {
    min-width: 0;
  }
`;

const ShowMoreRow = styled.div`
  margin-top: ${SPACE_XL}px;
  display: flex;
  justify-content: center;
`;

const ShowMoreButton = styled.button`
  background: ${COLOR_SURFACE_CONTAINER_STRONG};
  border: none;
  border-radius: ${GENERIC_ITEM_BORDER_RADIUS}px;
  padding: ${SPACE_MD}px ${SPACE_XL}px;
  font-size: 16px;
  font-weight: 400;
  color: ${COLOR_TEXT_PRIMARY};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${SPACE_SM}px;
`;

const FeedbackForm = styled.div`
  max-width: 600px;
  display: flex;
  flex-direction: column;
  gap: ${SPACE_SM}px;
  padding: ${SECTION_SPACING}px 0;
`;

const FeedbackControls = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACE_MD}px;
  color: ${COLOR_TEXT_SECONDARY};
`;

const StandardsBannerSlot = styled.div`
  padding: ${SECTION_SPACING}px 0;

  & > * {
    margin-bottom: 0;
  }
`;

const RetryRow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${SPACE_LG}px;
  padding: ${SPACE_XL}px 0;
  background: ${COLOR_SURFACE_CONTAINER};
  border-radius: ${GENERIC_ITEM_BORDER_RADIUS}px;
`;

type FeedbackStatus = "idle" | "submitting" | "sent" | "error";

export type UnitSearch = {
  query: string;
  setQuery: (query: string) => void;
  committedQuery: string | null;
  isSearching: boolean;
  hasError: boolean;
  results: SearchResponse | null;
  runSearch: () => void;
  performSearch: (query: string) => void;
  clearSearch: () => void;
  canGiveFeedback: boolean;
  isFeedbackFormOpen: boolean;
  toggleFeedbackForm: () => void;
  feedbackText: string;
  setFeedbackText: (text: string) => void;
  feedbackStatus: FeedbackStatus;
  submitFeedback: () => void;
  expandedGroups: ReadonlySet<number>;
  toggleGroupExpanded: (groupIndex: number) => void;
};

export const useUnitSearch = (isReady: boolean): UnitSearch => {
  const { search, submitSearchFeedback } = React.useContext(ApiContext);
  const { accountLiveData } = React.useContext(AccountContext);
  const history = useHistory();
  const location = useLocation();

  const [query, setQuery] = React.useState("");
  const [committedQuery, setCommittedQuery] = React.useState<string | null>(
    null
  );
  const [results, setResults] = React.useState<SearchResponse | null>(null);
  const [isSearching, setIsSearching] = React.useState(false);
  const [hasError, setHasError] = React.useState(false);
  const [expandedGroups, setExpandedGroups] = React.useState<
    ReadonlySet<number>
  >(new Set());
  const [isFeedbackFormOpen, setIsFeedbackFormOpen] = React.useState(false);
  const [feedbackText, setFeedbackText] = React.useState("");
  const [feedbackStatus, setFeedbackStatus] =
    React.useState<FeedbackStatus>("idle");
  const currentSearchJobUuid = React.useRef<string | null>(null);
  const currentFeedbackSubmitUuid = React.useRef<string | null>(null);

  const updateUrlWithQuery = React.useCallback(
    (newQuery: string | null) => {
      const newUrl = new URL(window.location.href);
      if (newQuery && newQuery.length > 0) {
        newUrl.searchParams.set(SEARCH_QUERY_PARAM, newQuery);
      } else {
        newUrl.searchParams.delete(SEARCH_QUERY_PARAM);
      }
      history.replace(newUrl.pathname + newUrl.search, history.location.state);
    },
    [history]
  );

  const resetFeedbackForm = React.useCallback(() => {
    currentFeedbackSubmitUuid.current = null;
    setIsFeedbackFormOpen(false);
    setFeedbackText("");
    setFeedbackStatus("idle");
  }, []);

  const performSearch = React.useCallback(
    async (rawQuery: string) => {
      const trimmedQuery = rawQuery.trim();
      if (trimmedQuery.length === 0) {
        return;
      }
      setCommittedQuery(trimmedQuery);
      updateUrlWithQuery(trimmedQuery);
      setExpandedGroups(new Set());
      setHasError(false);
      resetFeedbackForm();
      setIsSearching(true);
      const searchJobUuid = (currentSearchJobUuid.current = UUID.v4());
      const account = accountLiveData.value;
      const flags = account.hasUserAndMaybeProfile() ? account.user.flags : {};
      const response = await search(trimmedQuery, flags);
      if (currentSearchJobUuid.current !== searchJobUuid) {
        return;
      }
      if (response === undefined) {
        setResults(null);
        setHasError(true);
      } else {
        setResults(response);
      }
      setIsSearching(false);
    },
    [accountLiveData, search, updateUrlWithQuery, resetFeedbackForm]
  );

  const runSearch = React.useCallback(() => {
    performSearch(query);
  }, [performSearch, query]);

  const clearSearch = React.useCallback(() => {
    currentSearchJobUuid.current = null;
    setCommittedQuery(null);
    setResults(null);
    setIsSearching(false);
    setHasError(false);
    setQuery("");
    setExpandedGroups(new Set());
    updateUrlWithQuery(null);
    resetFeedbackForm();
  }, [updateUrlWithQuery, resetFeedbackForm]);

  React.useEffect(() => {
    const urlQuery = new URLSearchParams(location.search).get(
      SEARCH_QUERY_PARAM
    );
    if (!urlQuery && committedQuery !== null) {
      clearSearch();
    }
  }, [location.search, committedQuery, clearSearch]);

  const didRestoreFromUrl = React.useRef(false);
  React.useEffect(() => {
    if (didRestoreFromUrl.current || !isReady) {
      return;
    }
    didRestoreFromUrl.current = true;
    const urlQuery = new URLSearchParams(window.location.search).get(
      SEARCH_QUERY_PARAM
    );
    if (urlQuery && urlQuery.trim().length > 0) {
      setQuery(urlQuery);
      performSearch(urlQuery);
    }
  }, [isReady, performSearch]);

  const submitFeedback = React.useCallback(async () => {
    const searchId = results?.id;
    const feedback = feedbackText.trim();
    if (searchId == null || feedback.length === 0) {
      return;
    }
    setFeedbackStatus("submitting");
    const submitUuid = (currentFeedbackSubmitUuid.current = UUID.v4());
    const ok = await submitSearchFeedback(searchId, feedback);
    if (currentFeedbackSubmitUuid.current !== submitUuid) {
      return;
    }
    if (ok) {
      setFeedbackText("");
      setFeedbackStatus("sent");
    } else {
      setFeedbackStatus("error");
    }
  }, [feedbackText, results, submitSearchFeedback]);

  const updateFeedbackText = React.useCallback((text: string) => {
    setFeedbackText(text);
    setFeedbackStatus((status) =>
      status === "sent" || status === "error" ? "idle" : status
    );
  }, []);

  const toggleGroupExpanded = (groupIndex: number) => {
    setExpandedGroups((previous) => {
      const next = new Set(previous);
      if (next.has(groupIndex)) {
        next.delete(groupIndex);
      } else {
        next.add(groupIndex);
      }
      return next;
    });
  };

  const isBetaTester =
    accountLiveData.value.user?.full_access === FullAccess.BetaTester;

  return {
    query,
    setQuery,
    committedQuery,
    isSearching,
    hasError,
    results,
    runSearch,
    performSearch,
    clearSearch,
    canGiveFeedback: isBetaTester && !isSearching && results?.id != null,
    isFeedbackFormOpen,
    toggleFeedbackForm: () => setIsFeedbackFormOpen((open) => !open),
    feedbackText,
    setFeedbackText: updateFeedbackText,
    feedbackStatus,
    submitFeedback,
    expandedGroups,
    toggleGroupExpanded,
  };
};

export const UnitSearchResults: React.FC<{ search: UnitSearch }> = ({
  search,
}) => {
  const { t } = useTranslation();
  const { accountLiveData } = React.useContext(AccountContext);
  const account = accountLiveData.value;
  const [unitTilesRow, setUnitTilesRow] = React.useState<HTMLDivElement | null>(
    null
  );
  const unitColumnCount = useGridColumnCount(
    unitTilesRow,
    UNIT_TILE_MIN_WIDTH,
    SPACE_XL
  );

  const groups = (search.results?.results ?? [])
    .map((group) => ({
      kind: group.kind,
      title: group.title,
      items: group.results,
    }))
    .filter((group) => group.items.length > 0);

  const renderGroup = (
    group: { kind: SearchResultKind; title: string; items: (Unit | Lesson)[] },
    groupIndex: number
  ) => {
    const isUnitGroup = group.kind === SearchResultKind.Unit;
    const isExpanded = search.expandedGroups.has(groupIndex);
    // Units fill whole rows so the grid never collapses to a half-empty row, with
    // INITIAL_RESULTS_PER_SECTION as the floor.
    const collapsedCount =
      isUnitGroup && unitColumnCount > 0
        ? Math.ceil(INITIAL_RESULTS_PER_SECTION / unitColumnCount) *
          unitColumnCount
        : INITIAL_RESULTS_PER_SECTION;
    const hasMore = group.items.length > collapsedCount;
    const visibleItems =
      hasMore && !isExpanded
        ? group.items.slice(0, collapsedCount)
        : group.items;
    const hiddenCount = group.items.length - collapsedCount;

    return (
      <Section key={`${group.kind}-${groupIndex}`}>
        <SectionHeading>{group.title}</SectionHeading>
        {isUnitGroup ? (
          <UnitTilesRow ref={setUnitTilesRow}>
            {visibleItems.map((item) => (
              <UnitItem
                key={item.id}
                unit={item as Unit}
                loggingContext={UnitItemLoggingContext.Unit}
              />
            ))}
          </UnitTilesRow>
        ) : (
          <LessonsRow>
            {visibleItems.map((item) => (
              <LessonItem
                key={`${item.id}-${(item as Lesson).custom_flashpack_id}`}
                lesson={item as Lesson}
                unitId={null}
                account={account}
                leftCornerDecoration={LeftCornerDecoration.STANDARD}
                loggingContext={LessonItemLoggingContext.Lesson}
              />
            ))}
          </LessonsRow>
        )}
        {hasMore && (
          <ShowMoreRow>
            <ShowMoreButton
              onClick={() => search.toggleGroupExpanded(groupIndex)}
            >
              {t(isExpanded ? "search_hide" : "search_show_more", {
                count: hiddenCount,
              })}
              <FontAwesomeIcon
                icon={isExpanded ? faChevronUp : faChevronDown}
              />
            </ShowMoreButton>
          </ShowMoreRow>
        )}
      </Section>
    );
  };

  const renderBody = () => {
    if (search.isSearching) {
      return (
        <LoadingContainer>
          <LoadingIndicator size="4x" />
        </LoadingContainer>
      );
    }
    if (search.hasError) {
      return (
        <RetryRow>
          <span>{t("search_error")}</span>
          <PrimaryButton onClick={search.runSearch}>
            {t("search_retry")}
          </PrimaryButton>
        </RetryRow>
      );
    }
    if (groups.length === 0) {
      return (
        <EmptyResults
          message={t("search_no_results", { query: search.committedQuery })}
        />
      );
    }
    return groups.map(renderGroup);
  };

  return (
    <>
      {search.isFeedbackFormOpen && search.canGiveFeedback && (
        <FeedbackForm>
          <Form.Control
            as="textarea"
            rows={3}
            maxLength={FEEDBACK_MAX_LENGTH}
            placeholder={t("search_feedback_placeholder")}
            value={search.feedbackText}
            onChange={(e) => search.setFeedbackText(e.target.value)}
          />
          <FeedbackControls>
            <PrimaryButton
              onClick={search.submitFeedback}
              disabled={
                !search.feedbackText.trim() ||
                search.feedbackStatus === "submitting"
              }
            >
              {t("generic_send")}
            </PrimaryButton>
            {search.feedbackStatus === "sent" && (
              <span>{t("search_feedback_sent")}</span>
            )}
            {search.feedbackStatus === "error" && (
              <span>{t("search_feedback_error")}</span>
            )}
          </FeedbackControls>
        </FeedbackForm>
      )}

      {!search.isSearching &&
        !search.hasError &&
        (search.results?.standards?.length ?? 0) > 0 && (
          <StandardsBannerSlot>
            <SearchStandardsBanner
              groups={search.results!.standards!}
              onSearchCode={(code) => {
                search.setQuery(code);
                search.performSearch(code);
              }}
            />
          </StandardsBannerSlot>
        )}

      {renderBody()}
    </>
  );
};
